import PropTypes from 'prop-types'
import { forwardRef, useImperativeHandle, useRef } from 'react'
import './MfSwitch.scss'
import { UncontrolledTooltip } from 'reactstrap'
import { renderTooltip } from './FormInputs'

/**
 * @typedef {Object} MFSwitchProps
 *
 * @property {string} id
 * @property {boolean} disabled - Indicates whether or not the switch is disabled
 * @property {boolean} checked - Indicates whether or not the switch is checked
 * @property {string} label - The value of the switch element's label
 * @property {string} tooltip - The value of the switch's toolttip
 * @property {string} props.tooltipType - Can be 'hover' (default) or 'icon'
 * @property {string} props.labelPosition - Position of label to switch
 * @property {(checked: boolean) => void} onChange - Callback function to fire on change events
 */

/**
 * @type {MFSwitchProps}
 */
const propTypes = {
  /**
   * @default ''
   */
  id: PropTypes.string,

  /**
   * @default false
   */
  disabled: PropTypes.bool,

  /**
   * @default false
   */
  checked: PropTypes.bool,

  /**
   * @default false
   */
  value: PropTypes.bool,

  /**
   * @default ''
   */
  label: PropTypes.string,

  /**
   * @default ''
   */
  tooltip: PropTypes.string,

  /**
   * @default ()=>{}
   */
  onChange: PropTypes.func,

  /**
   * @default 'hover'
   */
  tooltipType: PropTypes.string,

  /**
   * @default ''
   */
  labelPosition: PropTypes.string
}

/**
 * @type {MFSwitchProps}
 */
const defaultProps = {
  id: '',
  checked: false,
  onChange: () => {},
  label: '',
  disabled: false,
  tooltip: '',
  tooltipType: 'hover',
  labelPosition: 'right'
}

/**
 * A custom switch component with optional tooltip.
 * @param {MFSwitchProps} props - The props for the component.
 * @returns {JSX.Element} The rendered switch component.
 */
const MfSwitch = forwardRef((props, ref) => {
  const labelRef = useRef(null)
  useImperativeHandle(
    ref,
    () => ({
      /**
       * Toggle the switch state
       */
      toggle: () => {
        props.onChange(!props.checked)
      }
    }),
    [props.onChange, props.checked]
  )

  const TooltipPopup = () => {
    return (
      <UncontrolledTooltip placement="top" target={labelRef} className="mfswitch-tooltip">
        {props.tooltip}
      </UncontrolledTooltip>
    )
  }

  // get unique identifier for component
  const componentId = Math.random().toString(36).substring(7)

  const renderlabel = () => {
    return (
      <>
        <label className={`switch-label ${props.labelPosition === 'left' ? 'mr-2' : ''}`}>
          {props.label}
          {props.tooltip && props.tooltipType === 'icon' && (
            <> {renderTooltip(`mfSwitchTooltip-${componentId}`, props.tooltip)}</>
          )}
        </label>
        {props.tooltip && props.tooltipType === 'hover' ? <TooltipPopup /> : null}
      </>
    )
  }

  return (
    <div className="switch-container d-flex align-items-center">
      {props.labelPosition === 'left' && renderlabel()}
      <label ref={labelRef} id={props.id} className="custom-switch">
        <input
          type="checkbox"
          className={`${props.disabled ? 'disabled' : ''}`}
          checked={Boolean(props.checked)}
          disabled={props.disabled}
          onChange={() => props.onChange(!props.checked)}
        />
        <span className={`${props.disabled ? 'disabled ' : ''}slider round`}></span>
      </label>
      {props.labelPosition === 'right' && renderlabel()}
    </div>
  )
})

MfSwitch.displayName = 'MFSwitch'
MfSwitch.propTypes = propTypes
MfSwitch.defaultProps = defaultProps

export default MfSwitch
